import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Info, AlertTriangle, AlertOctagon, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const LEVEL_META = {
  info: { icon: Info, cls: "bg-sky-100 text-sky-800 border-sky-200", iconCls: "text-sky-600" },
  warning: { icon: AlertTriangle, cls: "bg-amber-100 text-amber-800 border-amber-200", iconCls: "text-amber-600" },
  urgent: { icon: AlertOctagon, cls: "bg-red-100 text-red-800 border-red-200", iconCls: "text-red-600" },
};

export default function NotificationsPage() {
  const { locationId } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/notifications", { params: { location_id: locationId } })
      .then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.notification_id}/read`);
      load();
    } catch {}
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success(t("common.success"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-3xl md:pl-14" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("notif.title")}</h1>
        <Button data-testid="mark-all-read-button" variant="outline" onClick={markAll} className="h-11 tactile-btn">
          <CheckCheck className="h-4 w-4 mr-1" />{t("notif.markAllRead")}
        </Button>
      </div>

      <div className="space-y-2" data-testid="notifications-list">
        {items.map((n) => {
          const meta = LEVEL_META[n.level] || LEVEL_META.info;
          const Icon = meta.icon;
          return (
            <button key={n.notification_id} data-testid={`notification-${n.notification_id}`} onClick={() => markRead(n)}
              className={`tactile-btn w-full text-left bg-card border-2 rounded-2xl p-4 flex items-start gap-3 ${n.read ? "opacity-60 border-border" : meta.cls}`}>
              <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${meta.iconCls}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                <p className="text-[10px] text-muted-foreground font-mono mt-1">{format(new Date(n.created_at), "dd/MM/yyyy HH:mm")}</p>
              </div>
              {!n.read && <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0 mt-1.5" />}
            </button>
          );
        })}
        {!items.length && (
          <div className="text-center py-16 text-muted-foreground text-sm" data-testid="notifications-empty">
            <Bell className="h-10 w-10 mx-auto mb-2 opacity-40" />
            {t("notif.empty")}
          </div>
        )}
      </div>
    </div>
  );
}
